import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { fmtMins, fmtClock, ymdLocal, timeToMinutes } from '../lib/format.js';
import { locationKeyFromName, isoMondayOf } from '../lib/schedule.js';
import { TimecardReviewPanel } from './TimecardReviewPanel.jsx';
import { CorrectionRequestModal } from './CorrectionRequestModal.jsx';
import { timeclockEnabled } from '../lib/flags.js';

// ─── My Time Card view (own pay-period card; manager all-staff toggle) ───────
export function MyTimeCardView({ location, user, isManager }) {
  const [weekStart, setWeekStart] = useState(() => isoMondayOf(new Date()));
  const [scope, setScope] = useState('mine');   // 'mine' | 'all' (manager only)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  // CJ feedback 2026-06-15: staff who forgot to clock out had no way to
  // fix it from the time-card view — the existing "Request a correction"
  // affordance only lived on the punch banner (Home → Daily). Per-row
  // button here opens the same modal with sensible defaults so a staffer
  // can flag any historical shift without going home first.
  const [correctionDefaults, setCorrectionDefaults] = useState(null);
  const locationKey = locationKeyFromName(location?.name);
  const myId = user?.square_id || null;

  const load = useCallback(async () => {
    if (!locationKey) return;
    setLoading(true);
    try {
      const res = await api('GET', `/api/schedule/${locationKey}/week?week_start=${weekStart}`);
      setData(res); setError(null);
    } catch (e) { setError(e.message || 'Failed to load time card'); }
    finally { setLoading(false); }
  }, [locationKey, weekStart]);
  useEffect(() => { load(); }, [load]);

  const thisMon = isoMondayOf(new Date());
  function shiftWeek(weeks) {
    const d = new Date(weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    setWeekStart(ymdLocal(d));
  }

  // Build per-staff time-card rows. For "mine" scope, filter to current
  // user. Aggregates: scheduled hours / actual hours / variance.
  const days = data?.days || [];
  const byStaff = new Map();
  for (const d of days) {
    for (const s of d.shifts || []) {
      if (scope === 'mine' && (!myId || s.teamMemberId !== myId)) continue;
      const key = s.teamMemberId || s.employeeName;
      if (!byStaff.has(key)) byStaff.set(key, { name: s.employeeName, role: s.jobTitle, rows: [] });
      const sched = (timeToMinutes(s.scheduledEnd) - timeToMinutes(s.scheduledStart)) / 60;
      const actStart = timeToMinutes(s.actualStart);
      const actEnd   = timeToMinutes(s.actualEnd);
      const actual = (actStart != null && actEnd != null) ? (actEnd - actStart) / 60 : null;
      byStaff.get(key).rows.push({
        date: d.date,
        scheduledStart: s.scheduledStart, scheduledEnd: s.scheduledEnd,
        actualStart: s.actualStart || '', actualEnd: s.actualEnd || '',
        scheduledHours: Number.isFinite(sched) ? sched : 0,
        actualHours: actual,
        clockStatus: s.clockStatus,
        lateMinutes: s.lateMinutes || 0,
        earlyOutMinutes: s.earlyOutMinutes || 0,
      });
    }
  }
  const staffList = Array.from(byStaff.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return (
    <div className="space-y-3">
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(-1)}>← Prev</button>
          <button className="btn-secondary" onClick={() => setWeekStart(thisMon)} disabled={weekStart === thisMon}
                  style={{ padding: '6px 12px', opacity: weekStart === thisMon ? 0.5 : 1 }}>This week</button>
          <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(1)}>Next →</button>
          <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 600, color: '#37474f' }}>
            Week of {new Date(weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
        {isManager && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[{ id: 'mine', label: 'Mine' }, { id: 'all', label: 'All staff' }].map((m) => (
              <button key={m.id} onClick={() => setScope(m.id)}
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  background: scope === m.id ? 'var(--forest)' : '#fff',
                  color: scope === m.id ? '#fff' : '#37474f',
                  border: `1px solid ${scope === m.id ? 'var(--forest)' : '#d0d4d9'}`,
                  borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                }}>
                {m.label}
              </button>
            ))}
            {scope === 'all' && (
              <button onClick={() => setReviewOpen(true)}
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  background: 'var(--forest)', color: '#fff', border: '1px solid var(--forest)',
                  borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                  marginLeft: 4,
                }}>
                Review · {location.name}
              </button>
            )}
          </div>
        )}
      </div>

      {reviewOpen && (
        <TimecardReviewPanel
          location={location}
          weekStart={weekStart}
          currentUser={user}
          onClose={() => setReviewOpen(false)}
          onCertified={() => load()}
        />
      )}

      {loading && !data && <div className="card text-sm text-gray-500">Loading time card…</div>}
      {error && (() => {
        // W-006: surface the common "out of supported window" case as a
        // friendly empty state instead of a red-bordered error. The
        // /api/schedule/:loc/week endpoint rejects dates more than ~31
        // days back or ~28 days ahead — that's not really a failure,
        // just a date the staff member shouldn't be looking at.
        let friendly = error;
        try { const p = JSON.parse(error); friendly = p?.error || error; } catch {}
        const isOutOfRange = /range|window|too far|31 day|28 day/i.test(friendly);
        if (isOutOfRange) {
          return (
            <div className="card text-sm text-gray-500">
              Time-card history is available for the last 31 days and the next 28 days. Pick a week within that range.
            </div>
          );
        }
        return (
          <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
            <div className="text-sm font-semibold text-red-700">Time card unavailable</div>
            <div className="text-xs text-gray-600 mt-1">{friendly}</div>
          </div>
        );
      })()}

      {data && staffList.length === 0 && (
        <div className="card text-sm text-gray-500">
          {scope === 'mine' ? 'No shifts for you this week.' : 'No shifts scheduled this week.'}
        </div>
      )}

      {data && staffList.map((p) => {
        const totalSched = p.rows.reduce((s, r) => s + r.scheduledHours, 0);
        const totalActual = p.rows.reduce((s, r) => s + (r.actualHours || 0), 0);
        const variance = totalActual - totalSched;
        return (
          <div key={p.name} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>{p.name}</span>
                {p.role && <span style={{ fontSize: 11, color: '#9e9e9e', marginLeft: 6 }}>· {p.role}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#37474f' }}>
                Scheduled: <strong>{totalSched.toFixed(1)}h</strong>
                {' · '}
                Actual: <strong>{totalActual.toFixed(1)}h</strong>
                {' · '}
                <span style={{ color: variance > 0.05 ? '#ef6c00' : variance < -0.05 ? '#c62828' : '#2e7d32' }}>
                  Δ {variance >= 0 ? '+' : ''}{variance.toFixed(1)}h
                </span>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#757575', textAlign: 'left' }}>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Day</th>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Scheduled</th>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Actual</th>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Hours</th>
                    <th style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {p.rows.map((r) => {
                    const date = new Date(r.date + 'T12:00:00Z');
                    const dayLabel = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][date.getUTCDay()];
                    const notes = [];
                    if (r.clockStatus === 'not_clocked_in') notes.push({ text: 'no clock', color: '#c62828' });
                    if (r.lateMinutes > 5) notes.push({ text: `late ${fmtMins(r.lateMinutes)}`, color: '#ef6c00' });
                    if (r.earlyOutMinutes > 5) notes.push({ text: `early out ${fmtMins(r.earlyOutMinutes)}`, color: '#ef6c00' });
                    return (
                      <tr key={r.date} style={{ borderBottom: '1px solid #fafafa' }}>
                        <td style={{ padding: '6px 8px' }}>{dayLabel} {r.date.slice(5)}</td>
                        <td style={{ padding: '6px 8px' }}>
                          {r.scheduledStart && r.scheduledEnd ? `${fmtClock(r.scheduledStart)}–${fmtClock(r.scheduledEnd)}` : '—'}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          {r.actualStart ? `${fmtClock(r.actualStart)}–${r.actualEnd ? fmtClock(r.actualEnd) : 'still on'}` : '—'}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          {r.scheduledHours.toFixed(1)} / {r.actualHours != null ? r.actualHours.toFixed(1) : '—'}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                            {notes.map((n, i) => (
                              <span key={i} style={{ color: n.color, fontWeight: 600, fontSize: 11,
                                                      padding: '2px 8px', borderRadius: 99,
                                                      border: `1px solid ${n.color}`,
                                                      background: '#fff', whiteSpace: 'nowrap' }}>
                                {n.text}
                              </span>
                            ))}
                            {scope === 'mine' && timeclockEnabled() && (
                              <button onClick={() => {
                                // Defaults: if there's a clock-in but no clock-out
                                // (forgot to clock out) → edit_punch with proposed
                                // clock-in already filled in. If no clock-in (forgot
                                // to clock in entirely) → add_punch. Server resolves
                                // punch_id from shift_date when omitted.
                                const hasIn = !!r.actualStart;
                                const hasOut = !!r.actualEnd;
                                setCorrectionDefaults({
                                  shift_date: r.date,
                                  request_type: hasIn ? 'edit_punch' : 'add_punch',
                                  proposed_clock_in_local:  r.actualStart || r.scheduledStart || '09:00',
                                  proposed_clock_out_local: hasOut ? r.actualEnd : (r.scheduledEnd || ''),
                                  location_id: location?.id || null,
                                });
                              }} style={{ background: 'transparent', border: '1px dashed #d0d4d9', borderRadius: 4,
                                            padding: '2px 8px', fontSize: 11, color: '#5a6470', cursor: 'pointer',
                                            fontFamily: 'inherit' }}>
                                Request fix
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="card" style={{ fontSize: 11, color: '#757575', padding: 10 }}>
        Time clocks from Square Labor. Pay-period certification ships in a later phase.
      </div>

      {correctionDefaults && (
        <CorrectionRequestModal user={user}
          defaults={correctionDefaults}
          onClose={() => setCorrectionDefaults(null)}
          onSubmitted={() => { alert('Correction sent to your manager.'); load(); }} />
      )}
    </div>
  );
}
